import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth, googleProvider } from '../firebase';
import {
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

// ---------------------------------------------------------------------------
// AuroraCanvas — unchanged from original
// ---------------------------------------------------------------------------
const AuroraCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const blobs = [
      { cx: 0.20, cy: 0.22, r: 0.42, color: [42, 82, 168],   opacity: 0.45, sx: 0.28, sy: 0.21, px: 0.00, py: 0.00, amp: 0.13 },
      { cx: 0.78, cy: 0.72, r: 0.38, color: [66, 113, 197],   opacity: 0.38, sx: 0.22, sy: 0.31, px: 1.60, py: 2.40, amp: 0.11 },
      { cx: 0.50, cy: 0.48, r: 0.30, color: [100, 149, 220],  opacity: 0.30, sx: 0.19, sy: 0.26, px: 3.10, py: 1.00, amp: 0.09 },
      { cx: 0.15, cy: 0.78, r: 0.28, color: [180, 200, 240],  opacity: 0.35, sx: 0.24, sy: 0.18, px: 0.80, py: 3.50, amp: 0.10 },
    ];

    let raf: number;
    const draw = (ts: number) => {
      const t = ts * 0.001;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      ctx.fillStyle = '#e6edfa';
      ctx.fillRect(0, 0, w, h);

      blobs.forEach(b => {
        const x = (b.cx + Math.sin(t * b.sx + b.px) * b.amp) * w;
        const y = (b.cy + Math.cos(t * b.sy + b.py) * b.amp) * h;
        const r = b.r * Math.min(w, h);
        const [R, G, B] = b.color;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0,   `rgba(${R},${G},${B},${b.opacity})`);
        g.addColorStop(0.5, `rgba(${R},${G},${B},${b.opacity * 0.4})`);
        g.addColorStop(1,   `rgba(${R},${G},${B},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(x, y, r, r * 0.85, t * 0.05, 0, Math.PI * 2);
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
    />
  );
};

// ---------------------------------------------------------------------------
// Google SVG logo
// ---------------------------------------------------------------------------
const GoogleLogo: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
    <path d="M47.532 24.552c0-1.636-.132-3.2-.396-4.704H24.48v8.928h12.932c-.564 2.988-2.24 5.52-4.764 7.224v5.988h7.716c4.512-4.152 7.168-10.272 7.168-17.436z" fill="#4285F4"/>
    <path d="M24.48 48c6.48 0 11.916-2.148 15.888-5.82l-7.716-5.988c-2.148 1.44-4.896 2.304-8.172 2.304-6.276 0-11.592-4.236-13.5-9.936H3.024v6.18C6.984 42.816 15.168 48 24.48 48z" fill="#34A853"/>
    <path d="M10.98 28.56A14.4 14.4 0 0 1 10.2 24c0-1.584.276-3.12.78-4.56v-6.18H3.024A23.964 23.964 0 0 0 .48 24c0 3.876.936 7.548 2.544 10.74l7.956-6.18z" fill="#FBBC05"/>
    <path d="M24.48 9.504c3.54 0 6.708 1.212 9.204 3.6l6.888-6.888C36.396 2.388 30.96 0 24.48 0 15.168 0 6.984 5.184 3.024 13.26l7.956 6.18c1.908-5.7 7.224-9.936 13.5-9.936z" fill="#EA4335"/>
  </svg>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getEmailErrorMessage(code: string): string {
  switch (code) {
    case 'auth/account-exists-with-different-credential':
    case 'auth/wrong-password':
      return 'Ce compte utilise la connexion Google. Vous n\'avez pas configuré de mot de passe. Utilisez le bouton Google ci-dessus.';
    case 'auth/user-not-found':
      return 'Aucun compte trouvé avec cet email.';
    case 'auth/email-already-in-use':
      return 'Un compte existe déjà avec cet email. Connectez-vous.';
    case 'auth/weak-password':
      return 'Mot de passe trop court (6 caractères minimum).';
    case 'auth/invalid-email':
      return 'Adresse email invalide.';
    case 'auth/too-many-requests':
      return 'Trop de tentatives. Réessayez dans quelques minutes.';
    default:
      return 'Une erreur est survenue. Réessayez.';
  }
}

// ---------------------------------------------------------------------------
// Login component
// ---------------------------------------------------------------------------
const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const defaultMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';

  const [mode, setMode] = useState<'login' | 'signup'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // ── Mot de passe oublié ──────────────────────────────────────────────────
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Reset fields when switching modes
  const switchMode = (next: 'login' | 'signup') => {
    setMode(next);
    setError('');
    setPassword('');
    setConfirmPassword('');
  };

  // ------------------------------------------------------------------
  // Google auth
  // ------------------------------------------------------------------
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/alertes');
    } catch (e: any) {
      setError('Erreur de connexion Google. Réessayez.');
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Mot de passe oublié
  // ------------------------------------------------------------------
  const openForgot = () => {
    setForgotEmail(email);
    setForgotError('');
    setForgotSent(false);
    setShowForgot(true);
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailToReset = forgotEmail.trim();
    if (!emailToReset) { setForgotError('Veuillez saisir votre adresse email.'); return; }
    setForgotLoading(true);
    setForgotError('');
    try {
      await sendPasswordResetEmail(auth, emailToReset);
      setForgotSent(true);
    } catch (err: any) {
      const code: string = err?.code ?? '';
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        // Pour la sécurité, on affiche quand même un succès
        setForgotSent(true);
      } else {
        setForgotError('Impossible d\'envoyer l\'email. Réessayez.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Email / password auth
  // ------------------------------------------------------------------
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'signup' && password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        // Before attempting login, check if the account uses Google only
        try {
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (methods.length > 0 && methods.includes('google.com') && !methods.includes('password')) {
            setError('Ce compte utilise la connexion Google. Vous n\'avez pas configuré de mot de passe. Utilisez le bouton Google ci-dessus.');
            setLoading(false);
            return;
          }
        } catch {
          // fetchSignInMethodsForEmail can fail silently; proceed to signIn which will surface its own error
        }
        await signInWithEmailAndPassword(auth, email, password);
      }
      navigate('/alertes');
    } catch (e: any) {
      const code: string = e?.code ?? '';
      setError(getEmailErrorMessage(code));
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="min-h-screen flex">
      {/* ---- Left panel — aurora ---- */}
      <div className="hidden lg:flex lg:w-1/2 flex-col relative overflow-hidden">
        <AuroraCanvas />

        <div className="relative z-10 p-8 flex items-center gap-3">
          <img src="/assets/logo.png" alt="Aglomera" className="w-9 h-9 rounded-xl shadow-sm object-contain" />
          <span className="text-lg font-bold" style={{ color: '#2a52a8' }}>Aglomera</span>
        </div>

        <div className="relative z-10 flex-1 flex flex-col items-start justify-end p-12 pb-16">
          <p className="text-2xl font-bold text-gray-800 leading-snug max-w-xs">
            Ne ratez plus aucune offre.
          </p>
          <p className="mt-3 text-sm text-gray-500 max-w-xs leading-relaxed">
            Une newsletter quotidienne, personnalisée selon votre secteur. Zéro recherche, zéro effort.
          </p>
        </div>
      </div>

      {/* ---- Right panel — form ---- */}
      <div className="w-full lg:w-1/2 bg-white flex flex-col min-h-screen">
        <div className="p-6">
          <a
            href="https://aglomera.com"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors"
          >
            ← Accueil
          </a>
        </div>

        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-sm">
            {/* Logo + title */}
            <div className="flex flex-col items-center text-center mb-6">
              <img
                src="/assets/logo.png"
                alt="Aglomera"
                className="w-14 h-14 rounded-2xl mb-4 shadow-md object-contain"
                style={{ background: '#e6edfa', padding: '8px' }}
              />
              <h1 className="text-2xl font-bold text-gray-900 mb-1.5">Bienvenue sur Aglomera</h1>
            </div>

            {/* Mode tabs */}
            <div className="flex rounded-xl border border-gray-200 overflow-hidden mb-6">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="flex-1 py-2.5 text-sm font-semibold transition-colors"
                style={
                  mode === 'login'
                    ? { background: '#2a52a8', color: '#fff' }
                    : { background: '#fff', color: '#6b7280' }
                }
              >
                Se connecter
              </button>
              <button
                type="button"
                onClick={() => switchMode('signup')}
                className="flex-1 py-2.5 text-sm font-semibold transition-colors"
                style={
                  mode === 'signup'
                    ? { background: '#2a52a8', color: '#fff' }
                    : { background: '#fff', color: '#6b7280' }
                }
              >
                S'inscrire
              </button>
            </div>

            {/* Error banner */}
            {error && (
              <div
                className="mb-4 p-3 rounded-xl text-sm"
                style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}
              >
                {error}
              </div>
            )}

            {/* Google button */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-semibold flex items-center justify-center gap-3 disabled:opacity-50 shadow-sm"
            >
              {loading ? (
                <RefreshCw size={20} className="animate-spin" />
              ) : (
                <>
                  <GoogleLogo />
                  <span>Continuer avec Google</span>
                </>
              )}
            </button>

            {/* Separator */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">ou</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Email / password form */}
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
              <input
                type="email"
                required
                placeholder="Adresse email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 transition-colors disabled:opacity-50"
                style={{ background: '#fafafa' }}
              />
              <input
                type="password"
                required
                placeholder="Mot de passe"
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 transition-colors disabled:opacity-50"
                style={{ background: '#fafafa' }}
              />
              {mode === 'signup' && (
                <input
                  type="password"
                  required
                  placeholder="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 transition-colors disabled:opacity-50"
                  style={{ background: '#fafafa' }}
                />
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 shadow-sm"
                style={{ background: '#2a52a8' }}
              >
                {loading ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : mode === 'login' ? (
                  'Se connecter'
                ) : (
                  "S'inscrire"
                )}
              </button>
            </form>

            {/* Mot de passe oublié — lien visible uniquement en mode connexion */}
            {mode === 'login' && !showForgot && (
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={openForgot}
                  className="text-sm transition-opacity hover:opacity-70"
                  style={{ color: '#2a52a8' }}
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            {/* Formulaire de réinitialisation inline */}
            {mode === 'login' && showForgot && (
              <div
                className="mt-4 p-4 rounded-2xl border"
                style={{ borderColor: '#c7d7f5', background: '#f8faff' }}
              >
                {forgotSent ? (
                  <>
                    <p className="text-sm font-semibold text-green-700 mb-1">Email envoyé !</p>
                    <p className="text-xs text-green-600 leading-relaxed">
                      Un lien de réinitialisation a été envoyé à <strong>{forgotEmail}</strong>.
                      Pensez à vérifier vos spams.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setShowForgot(false); setForgotSent(false); }}
                      className="mt-3 text-xs hover:underline"
                      style={{ color: '#2a52a8' }}
                    >
                      ← Retour à la connexion
                    </button>
                  </>
                ) : (
                  <form onSubmit={handleForgotSubmit}>
                    <p className="text-sm font-semibold text-gray-800 mb-1">Réinitialiser le mot de passe</p>
                    <p className="text-xs text-gray-400 mb-3 leading-relaxed">
                      Saisissez votre adresse email et nous vous enverrons un lien pour définir un nouveau mot de passe.
                    </p>
                    <input
                      type="email"
                      required
                      placeholder="Adresse email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      disabled={forgotLoading}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 transition-colors mb-3 disabled:opacity-50"
                      style={{ background: '#fafafa' }}
                    />
                    {forgotError && (
                      <p className="text-xs text-red-600 mb-2">{forgotError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={forgotLoading}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
                        style={{ background: '#2a52a8' }}
                      >
                        {forgotLoading ? <RefreshCw size={15} className="animate-spin" /> : 'Envoyer le lien'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForgot(false); setForgotError(''); }}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Legal */}
            <p className="mt-5 text-center text-xs text-gray-400 leading-relaxed">
              En continuant, tu acceptes nos{' '}
              <Link
                to="/cgu"
                className="underline decoration-2 underline-offset-2 hover:opacity-80 transition-opacity"
                style={{ color: '#2a52a8' }}
              >
                Conditions d'utilisation
              </Link>
              {' '}et notre{' '}
              <Link
                to="/confidentialite"
                className="underline decoration-2 underline-offset-2 hover:opacity-80 transition-opacity"
                style={{ color: '#2a52a8' }}
              >
                Politique de confidentialité
              </Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
